import { SITE_DOMAIN, SITE_NAME } from './site';

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  {
    label: 'Family Tree Maker',
    to: '/family-tree-maker-2024',
    children: [
      { label: 'Family Tree Maker 2024', to: '/family-tree-maker-2024' },
      { label: 'Family Tree Maker 2019', to: '/family-tree-maker-2019' },
    ],
  },
  { label: 'Support Services', to: '/support-services' },
  { label: 'Guides', to: '/guides' },
  { label: 'FAQs', to: '/faqs' },
  { label: 'About', to: '/about-us' },
  { label: 'Contact', to: '/contact-us' },
];

export const POLICY_LINKS = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms & Conditions', to: '/terms-and-conditions' },
  { label: 'Refund Policy', to: '/refund-policy' },
  { label: 'Cookie Policy', to: '/cookie-policy' },
  { label: 'Disclaimer', to: '/disclaimer' },
];

export const SERVICES = [
  {
    icon: 'seed',
    title: 'Installation & Setup',
    text: 'First-time installs, reinstalls, and upgrades on Windows 10, Windows 11, and macOS — including the stubborn ones that stop half-way.',
  },
  {
    icon: 'key',
    title: 'Activation & Licensing',
    text: 'Rejected serial numbers, activation limits, and upgrade keys that will not unlock the edition you bought.',
  },
  {
    icon: 'sync',
    title: 'FamilySync & Ancestry',
    text: 'Stuck syncs, conflict messages, login loops, and trees that drift out of step with your Ancestry account.',
  },
  {
    icon: 'cloud',
    title: 'TreeVault & Backups',
    text: 'Off-site copies, scheduled backup habits, and safe restores when a computer fails or is replaced.',
  },
  {
    icon: 'photo',
    title: 'Photos & Media',
    text: 'Broken-link icons, relinking hundreds of files at once, and organising scans so they never go missing again.',
  },
  {
    icon: 'book',
    title: 'Charts, Reports & Books',
    text: 'Wall charts for reunions, printed family books, and PDF exports that look right on paper.',
  },
];

export const PROCESS = [
  { title: 'Reach out', text: 'Open live chat or send the contact form and tell us what is happening.' },
  { title: 'We diagnose', text: 'A technician asks about your edition, computer, and recent changes.' },
  { title: 'We fix it together', text: 'Guided steps in chat, or a remote session only if you agree.' },
  { title: 'You stay protected', text: 'We finish by checking your backups so it does not happen again.' },
];

export const PROMISES = [
  { title: 'Backup before any fix', text: 'We confirm your research is safely copied before changing anything.' },
  { title: 'Price before work', text: 'Free guidance first. If a paid session is needed, you hear the cost up front.' },
  { title: 'You stay in control', text: 'Remote access only with your permission — and you can end it any time.' },
  { title: 'Windows & Mac', text: 'From FTM 2014 and 2017 through 2019 and 2024, on both platforms.' },
];

export const FAQ_GROUPS = [
  {
    id: 'general',
    title: 'Getting Started',
    items: [
      {
        q: 'What is Family Tree Maker?',
        a: 'Family Tree Maker is desktop genealogy software for Windows and Mac. Your people, dates, places, photos, and sources are stored in a tree file on your own computer, and you can optionally sync with Ancestry.',
      },
      {
        q: 'Do I need to be online to use it?',
        a: 'Only for activation, updates, syncing, and online record hints. Entering relatives, attaching photos, and printing charts all work offline.',
      },
      {
        q: 'Can I import a tree from another genealogy program?',
        a: 'Usually yes. Export a GEDCOM file from the other program and import it into Family Tree Maker. Names, dates, and relationships transfer well; some media links and custom fields may need tidying afterwards.',
      },
      {
        q: 'Which edition should I be using?',
        a: 'Family Tree Maker 2024 is the edition currently being updated by the publisher. FTM 2019 still runs well for many people. If you are on a brand-new computer or operating system, 2024 is usually the smoothest choice.',
      },
    ],
  },
  {
    id: 'sync',
    title: 'Sync, Cloud & Media',
    items: [
      {
        q: 'Why has my FamilySync stopped working?',
        a: 'The most common causes are an outdated program version, an unstable connection, security software blocking the program, or conflicting edits made in both places between syncs. Our FamilySync guide walks through each one.',
      },
      {
        q: 'Why do my photos show a broken picture icon?',
        a: 'The program remembers where each file was stored. If the file was moved or renamed, or the drive is not connected, the link breaks. Find Missing Media in the Media workspace reconnects them.',
      },
      {
        q: 'What is the difference between TreeVault and FamilySync?',
        a: 'FamilySync keeps your desktop tree matched with an Ancestry tree. TreeVault simply stores copies of your tree file online so you can restore it later. Many people use both.',
      },
    ],
  },
  {
    id: 'service',
    title: 'About Our Service',
    items: [
      {
        q: `Is ${SITE_NAME} part of the company that makes Family Tree Maker?`,
        a: `No. ${SITE_DOMAIN} is an independent technical support service. We are not affiliated with Software MacKiev, Ancestry, or FamilySearch. The publisher offers its own official support on its website.`,
      },
      {
        q: 'How do I reach a technician?',
        a: 'Click Live Chat on any page — it is available around the clock — or send the form on our Contact page and we will reply by email or a callback at a time that suits you.',
      },
      {
        q: 'Do you charge for help?',
        a: 'All guides and FAQs on this website are free. Some one-to-one support sessions may be chargeable; we always tell you the price and get your agreement before any paid work begins.',
      },
      {
        q: 'Is remote access safe?',
        a: 'Remote sessions only start with your permission, you can watch everything that happens, and you can end the session at any moment. Many issues are solved in chat without remote access at all.',
      },
      {
        q: 'What if my problem is not fixed?',
        a: 'If a paid issue is not resolved, you can request a refund as described in our Refund Policy.',
      },
    ],
  },
];

export const HOME_FAQS = [FAQ_GROUPS[0].items[0], FAQ_GROUPS[1].items[0], FAQ_GROUPS[2].items[0], FAQ_GROUPS[2].items[2]];

export const DISCLAIMER_TEXT = `${SITE_DOMAIN} is an independent provider of technical support for Family Tree Maker users. We are not affiliated with, endorsed by, or sponsored by Software MacKiev, Ancestry, FamilySearch, or any other company mentioned on this website. All product names, logos, and trademarks belong to their respective owners and are used only to identify the software we support. Official support is available directly from the software publisher.`;
